const PUBLISHED_APP_ORIGIN = "https://soundcheckclub.com";

function normalizePath(path: string) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Resolve the public-facing app origin for ANY shareable link.
 *
 * Critically: inside the Capacitor native shell `window.location.origin`
 * is `capacitor://localhost`, and on Lovable preview hosts it is a preview
 * subdomain. Neither can be opened by recipients, so we always fall back to
 * the published production origin in those cases. We only use the live
 * `window.location.origin` when we're genuinely running on a public http(s)
 * web host (real web dev or the production site).
 */
export function getPublicAppUrl() {
  if (typeof window === "undefined") return PUBLISHED_APP_ORIGIN;

  // Capacitor native runtime — never share capacitor://localhost.
  const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
  if (isNative) return PUBLISHED_APP_ORIGIN;

  const { protocol, hostname, origin } = window.location;

  // Only http(s) origins are shareable; anything else (capacitor:, file:, etc.)
  // falls back to production.
  if (protocol !== "http:" && protocol !== "https:") return PUBLISHED_APP_ORIGIN;

  const isPreviewHost =
    hostname.includes("id-preview--") ||
    hostname.endsWith(".lovableproject.com") ||
    hostname.endsWith(".lovable.app") ||
    hostname.includes("-preview--") ||
    hostname.startsWith("preview--");

  if (isPreviewHost) return PUBLISHED_APP_ORIGIN;

  return origin;
}

/** Back-compat alias — same resolution as getPublicAppUrl(). */
export function getShareAppOrigin() {
  return getPublicAppUrl();
}

export function getCanonicalUrl(path: string) {
  return `${getPublicAppUrl()}${normalizePath(path)}`;
}

/** Canonical, human-facing review URL (the SPA route). */
export function getReviewCanonicalUrl(reviewId: string) {
  return getCanonicalUrl(`/reviews/${reviewId}`);
}

/**
 * Origin that serves crawler-readable Open Graph HTML for shared links.
 *
 * Static SPA hosting (Lovable) cannot server-render per-review OG tags, so the
 * dynamic OG HTML is produced by the `share-review` edge function. By default
 * that lives on the project's `*.supabase.co` host, which is why recipients see
 * a `supabase.co` domain.
 *
 * To present a SoundCheck-owned domain instead (e.g. iMessage showing
 * `link.soundcheckclub.com`), enable the Supabase Custom Domain add-on and map
 * a subdomain to this project, then set this constant to that origin
 * (e.g. "https://link.soundcheckclub.com"). Everything else keeps working —
 * the function path and params are unchanged.
 */
const SHARE_FUNCTION_ORIGIN = "https://dzzszwgoblulvhapnfbq.supabase.co";

/**
 * Shareable review URL.
 *
 * Static SPA hosting returns the generic index.html (no SSR), so crawlers
 * like iMessage/Slack/Discord never see review-specific Open Graph tags when
 * pointed straight at /reviews/:id. Instead we share the `share-review` edge
 * function's HTML metadata page: it returns a clean `text/html` document (no
 * attachment headers) whose `og:image` points to a dedicated raster image
 * route. Crawlers read the review-specific OG/Twitter tags server-side, while
 * real browsers are redirected to the canonical review page via JS replace.
 */
export function getReviewShareUrl(reviewId: string) {
  // Share the standard SoundCheck canonical review URL directly. We previously
  // routed through the `share-review` edge function for crawler OG tags, but
  // that surfaced a raw `*.supabase.co` "Text Document" preview in iMessage.
  // The normal SoundCheck preview is preferred over that.
  const canonical = getReviewCanonicalUrl(reviewId);
  console.log("[ShareDebug] Generated review share URL", canonical);
  return canonical;
}

/** Canonical public review URL from a review-like object (id required). */
export function getCanonicalReviewUrl(review: { id: string }) {
  return getReviewShareUrl(review.id);
}

export function getConcertShareUrl(concertId: string) {
  return getCanonicalUrl(`/concerts/${concertId}`);
}

export function getProfileShareUrl(username: string) {
  return getCanonicalUrl(`/profile/${username}`);
}

/** Canonical entity share URLs — always the clean slug route, no query params. */
export function getArtistShareUrl(slugOrId: string) {
  return getCanonicalUrl(`/artists/${slugOrId}`);
}

export function getTourShareUrl(slugOrId: string) {
  return getCanonicalUrl(`/tours/${slugOrId}`);
}

export function getVenueShareUrl(slug: string) {
  return getCanonicalUrl(`/venues/${slug}`);
}

export function getListShareUrl(listId: string) {
  return getCanonicalUrl(`/lists/${listId}`);
}
